import type { GoldKtLatestResponse } from '../api/client'

/**
 * Gold K_t live overlay bridge (2026-08-31/09-01 build, PR #311 + the Task 5
 * 5-min refresher in GoldKtSynthNavPublisherService). /api/gold/kt-latest
 * optionally carries overlayFrac/overlayBeta/overlayConfidence/
 * overlayGoldAsOfUtc/overlayGoldLastBarUtc/overlayComputedAtUtc/
 * combinedReturnFrac during overlay-eligible windows (pre-07:35 UK,
 * weekends/holidays, restored payloads) — absent otherwise.
 * combinedReturnFrac = (1+returnFrac)*(1+overlayFrac)-1.
 *
 * deriveGoldOverlay is the single source of truth for all three signal-page
 * surfaces (GoldSignalPanel line + explainer, GoldSparklineBar's combined
 * day-return, HoldingsPanel's footer) — same active-gate semantics as the
 * P&L bridge, so the signal page never disagrees with the P&L. Defensive by
 * design: any missing/non-numeric field degrades gracefully, never throws.
 */
export interface GoldOverlayView {
  /** overlayFrac AND overlayBeta both present and numeric. */
  active: boolean
  /** combinedReturnFrac if numeric, else null. */
  combinedFrac: number | null
  overlayFrac: number | null
  beta: number | null
  /** overlayFrac / beta (null when beta is 0/absent — division guard). */
  goldMoveFrac: number | null
  /** 'ok' | 'clamped' from overlayConfidence; null when absent/unrecognised. */
  badge: 'ok' | 'clamped' | null
  /** now - overlayGoldLastBarUtc, in minutes; null when unavailable/unparsable. */
  barAgeMinutes: number | null
  barTimeUtc: string | null
}

const INACTIVE: GoldOverlayView = {
  active: false,
  combinedFrac: null,
  overlayFrac: null,
  beta: null,
  goldMoveFrac: null,
  badge: null,
  barAgeMinutes: null,
  barTimeUtc: null,
}

function asFiniteNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

export function deriveGoldOverlay(
  kt: GoldKtLatestResponse | null | undefined,
  nowUtc: Date = new Date(),
): GoldOverlayView {
  if (!kt) return { ...INACTIVE }

  const overlayFrac = asFiniteNumber(kt.overlayFrac)
  const beta = asFiniteNumber(kt.overlayBeta)
  const active = overlayFrac != null && beta != null
  if (!active) return { ...INACTIVE }

  const combinedFrac = asFiniteNumber(kt.combinedReturnFrac)
  const goldMoveFrac = beta !== 0 ? overlayFrac / beta : null

  const confidence = kt.overlayConfidence
  const badge: 'ok' | 'clamped' | null =
    confidence === 'ok' ? 'ok' : confidence === 'beta_clamped' ? 'clamped' : null

  const barTimeUtc = typeof kt.overlayGoldLastBarUtc === 'string' ? kt.overlayGoldLastBarUtc : null
  let barAgeMinutes: number | null = null
  if (barTimeUtc) {
    const barDate = new Date(barTimeUtc)
    if (!Number.isNaN(barDate.getTime())) {
      barAgeMinutes = (nowUtc.getTime() - barDate.getTime()) / 60_000
    }
  }

  return { active, combinedFrac, overlayFrac, beta, goldMoveFrac, badge, barAgeMinutes, barTimeUtc }
}

/**
 * Shared signed-percentage formatter for the overlay surfaces — 2dp,
 * explicit '+' sign, em-dash for null (matches GoldSignalPanel's
 * ktRetDisplay style). Factored out (fix-wave 2026-09-02) so
 * GoldSignalPanel and HoldingsPanel don't each carry an identical
 * fmtPct/fmtOverlayPct.
 */
export function formatOverlayPct(v: number | null): string {
  if (v == null) return '—'
  return `${v >= 0 ? '+' : ''}${(v * 100).toFixed(2)}%`
}
